// import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/useAuthStore';
import TopBar from '@/components/TopBar';
import SystemMonitor from '@/components/SystemMonitor';

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user, logout, isLoading } = useAuthStore();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleLogoutAll = async () => {
    if (window.confirm('Logout from all devices?')) {
      try {
        await logout(true);
        navigate('/login');
      } catch (error) {
        console.error('Logout all failed:', error);
      }
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Top Navigation Bar */}
      <TopBar />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* System Monitor */}
        <div className="mb-8">
          <SystemMonitor />
        </div>

        {/* Quick Actions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {/* Video Library Card */}
          <button
            onClick={() => navigate('/videos')}
            className="bg-white rounded-lg shadow-md border border-gray-200 p-6 hover:shadow-lg transition-shadow text-left group"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="bg-blue-100 rounded-lg p-3 group-hover:bg-blue-200 transition-colors">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
              <svg className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Video Library</h3>
            <p className="text-sm text-gray-600">
              Upload, manage, and organize your video content library
            </p>
          </button>

          {/* Channels Card */}
          <button
            onClick={() => navigate('/channels')}
            className="bg-white rounded-lg shadow-md border border-gray-200 p-6 hover:shadow-lg transition-shadow text-left group"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="bg-green-100 rounded-lg p-3 group-hover:bg-green-200 transition-colors">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" />
                </svg>
              </div>
              <svg className="w-5 h-5 text-gray-400 group-hover:text-green-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Channels</h3>
            <p className="text-sm text-gray-600">
              Create and manage broadcast channels with HLS/RTMP output
            </p>
          </button>

          {/* Schedule Card */}
          <button
            onClick={() => navigate('/schedules')}
            className="bg-white rounded-lg shadow-md border border-gray-200 p-6 hover:shadow-lg transition-shadow text-left group"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="bg-purple-100 rounded-lg p-3 group-hover:bg-purple-200 transition-colors">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <svg className="w-5 h-5 text-gray-400 group-hover:text-purple-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Schedules</h3>
            <p className="text-sm text-gray-600">
              Create and manage video playout schedules with calendar view
            </p>
          </button>
        </div>

        {/* User Information Card */}
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-8 mb-8">
          <h3 className="text-xl font-bold text-gray-900 mb-6">
            Your Account Information
          </h3>
          <div className="space-y-4">
            <div className="flex items-start">
              <div className="w-32 flex-shrink-0">
                <span className="text-sm font-medium text-gray-500">
                  Full Name:
                </span>
              </div>
              <div className="flex-1">
                <span className="text-sm text-gray-900">{user.full_name}</span>
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-32 flex-shrink-0">
                <span className="text-sm font-medium text-gray-500">
                  Email:
                </span>
              </div>
              <div className="flex-1">
                <span className="text-sm text-gray-900">{user.email}</span>
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-32 flex-shrink-0">
                <span className="text-sm font-medium text-gray-500">Role:</span>
              </div>
              <div className="flex-1">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {user.role === 'admin'
                    ? 'Administrator'
                    : user.role === 'channel_operator'
                    ? 'Channel Operator'
                    : 'Viewer'}
                </span>
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-32 flex-shrink-0">
                <span className="text-sm font-medium text-gray-500">
                  Email Verified:
                </span>
              </div>
              <div className="flex-1">
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                    user.email_verified
                      ? 'bg-green-100 text-green-800'
                      : 'bg-yellow-100 text-yellow-800'
                  }`}
                >
                  {user.email_verified ? 'Verified' : 'Not Verified'}
                </span>
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-32 flex-shrink-0">
                <span className="text-sm font-medium text-gray-500">
                  User ID:
                </span>
              </div>
              <div className="flex-1">
                <span className="text-sm text-gray-600 font-mono">
                  {user.id}
                </span>
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-32 flex-shrink-0">
                <span className="text-sm font-medium text-gray-500">
                  Member Since:
                </span>
              </div>
              <div className="flex-1">
                <span className="text-sm text-gray-600">
                  {new Date(user.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Actions Card */}
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-8">
          <h3 className="text-xl font-bold text-gray-900 mb-6">
            Account Actions
          </h3>
          <div className="space-y-4">
            <button
              onClick={handleLogout}
              disabled={isLoading}
              className="w-full bg-gray-600 hover:bg-gray-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium px-4 py-2 rounded-lg transition-colors text-left"
            >
              Logout from this device
            </button>
            <button
              onClick={handleLogoutAll}
              disabled={isLoading}
              className="w-full bg-red-600 hover:bg-red-700 disabled:bg-red-400 disabled:cursor-not-allowed text-white font-medium px-4 py-2 rounded-lg transition-colors text-left"
            >
              Logout from all devices
            </button>
          </div>
          <p className="mt-4 text-xs text-gray-500">
            Note: More features will be added as development progresses.
          </p>
        </div>
      </main>
    </div>
  );
}
